import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, Modal, Radio, Row, Space, Spin, Table, Switch, Select } from "antd";
import { useFormik } from "formik";
import { get, toString } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../../components/functional_components/Button/PFButton";
import PfpService from "../../../services/api/PfpService";
import { setFilters } from "../../../redux/ducks/Filters";
import { setLoading } from "../../../redux/ducks/Loading";
import { paginationDefaults, productCategoryTypes, unitPriceTypes } from "../../../utils/properties";
import { buildQueryString, formatNumber, showError, showSuccess, trimData } from "../../../utils/utilities";
import ProductCategory from "../../../services/api/ProductCategory";
import CostCentre from "../../../services/api/CostCentre";

const { confirm } = Modal;
const { Option } = Select;

const Services = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const [state, setState] = useState({
    tableContent: [],
    selectedService: {},
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    productCategories: [],
    costCentres: [],
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    getServices(filters.string);
    getCostCentres();
  }, []);

  const getProdCategories = async (type) => {
    const productCategories = await ProductCategory.productCategories(`allPages=true&type=${type}`);
    if (!productCategories.error && productCategories.data) {
      productCategories.data.forEach((data, idx) => {
        data.key = idx; // // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({ ...prevState, productCategories: productCategories.data }));
    } else {
      showError(get(productCategories, "error.messageCode", "ER000"));
    }
  };

  const formikServices = useFormik({
    initialValues: {
      id: get(state.selectedService, "id", ""),
      name: get(state.selectedService, "name", ""),
      basePrice: get(state.selectedService, "basePrice", ""),
      unitPrice: get(state.selectedService, "unitPrice", ""),
      type: get(state.selectedService, "type", ""),
      enabled: get(state.selectedService, "enabled", false),
      productCategoryId: get(state.selectedService, "productCategoryId", ""),
      costCentreId: get(state.selectedService, "costCentreId", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.type) {
        errors.type = "Required!";
      }
      if (!values.basePrice && values.unitPrice === "HOUR") {
        errors.basePrice = "Required!";
      }
      if (!values.unitPrice) {
        errors.unitPrice = "Required!";
      }
      if (!values.productCategoryId) {
        errors.productCategoryId = "Required!";
      }
      if (!values.costCentreId) {
        errors.costCentreId = "Required!";
      }

      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      if (values.unitPrice === "PERCENT") {
        values.basePrice = "";
      }
      const saveResponse = await PfpService.saveService(values);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        getServices(filters.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const getServices = async (queryString) => {
    dispatch(setLoading(true));
    const services = await PfpService.getServicesList(queryString);
    if (!services.error && services.data) {
      services.data.forEach((service, i) => {
        service.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(services, "data", []),
        pagination: {
          currentPage: services.currentPage ? services.currentPage + 1 : paginationDefaults.page,
          size: get(services, "pageSize", paginationDefaults.size),
          totalItems: get(services, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const getCostCentres = async () => {
    dispatch(setLoading(true));
    const costCenters = await CostCentre.costCentre(`allPages=true`);
    if (!costCenters.error && costCenters.data) {
      setState((prevState) => ({
        ...prevState,
        costCentres: get(costCenters, "data", []),
      }));
    } else {
      showError(get(costCenters, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    {
      title: t("services.table.name"),
      dataIndex: "id",
      key: "id",
      render: (value, row) => {
        return `${value} | ${row.name}`;
      },
    },
    { title: t("services.table.unitPrice"), dataIndex: "unitPrice" },
    {
      title: t("services.table.basePrice"),
      dataIndex: "basePrice",
      key: "basePrice",
      responsive: ["md"],
      className: "table-value-right",
      render: (value) => (value !== null ? formatNumber({ value, lang: currentLanguage }) : "-"),
    },
    { title: t("services.table.type"), dataIndex: "type", key: "type", responsive: ["md"] },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(record.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            {!record.deletable ? (
              <Col>
                {" "}
                <DeleteOutlined className="icon-medium disabled no-pointer" />{" "}
              </Col>
            ) : (
              <Col onClick={() => deleteCallback(record.id)}>
                {" "}
                <DeleteOutlined className="icon-medium" />
              </Col>
            )}
          </div>
        );
      },
    },
  ];

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const serviceData = await PfpService.serviceData(id);
    if (!serviceData.error && serviceData.data) {
      getProdCategories(serviceData.data.unitPrice);
      setState((prevState) => ({ ...prevState, selectedService: serviceData.data }));
    } else {
      showError(get(serviceData, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = async (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await PfpService.deleteService(id);
        if (!deleteResponse.error) {
          showSuccess();
          getServices(filters.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    formikServices.resetForm();
    setState((prevState) => ({
      ...prevState,
      selectedService: {},
      productCategories: [],
      modal: { visible: false, type: state.modal.type },
    }));
  };

  const changeUnitPrice = (e) => {
    formikServices.handleChange(e);
    formikServices.setFieldValue("productCategoryId", "");
    getProdCategories(e.target.value);
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: toString(page - 1), size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              currentPage: page,
              size: pageSize,
              totalItems: state.pagination.totalItems,
            }));
            getServices(queryString.string);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />

      <Modal
        width={700}
        title={state.modal.type === "edit" ? state.selectedService.id : t("services.new")}
        open={state.modal.visible}
        onCancel={closeModal}
        footer={[
          <Button
            key="okButton"
            isDisabled={
              !(formikServices.dirty && formikServices.isValid) ||
              (formikServices.values.unitPrice === "HOUR" && Number(formikServices.values.basePrice) < 0)
            }
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikServices.submitForm}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="id">
                  {t("services.table.id")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Input
                  required
                  id="id"
                  name="id"
                  onChange={formikServices.handleChange}
                  onBlur={formikServices.handleBlur}
                  value={formikServices.values.id}
                  disabled={state.modal.type === "edit"}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="name">
                  {t("services.table.name")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Input
                  required
                  id="name"
                  name="name"
                  onChange={formikServices.handleChange}
                  onBlur={formikServices.handleBlur}
                  value={formikServices.values.name}
                />
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="unitPrice">
                  {t("services.table.unitPrice")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Radio.Group
                  id="unitPrice"
                  name="unitPrice"
                  onChange={changeUnitPrice}
                  value={formikServices.values.unitPrice}
                >
                  <Space direction="vertical">
                    {productCategoryTypes.map((productCategory) => {
                      return (
                        <Radio key={productCategory.value} value={productCategory.value}>
                          {t(`productCategories.${productCategory.label}`)}
                        </Radio>
                      );
                    })}
                  </Space>
                </Radio.Group>
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="type">
                  {t("services.table.type")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Radio.Group
                  id="type"
                  name="type"
                  onChange={formikServices.handleChange("type")}
                  value={formikServices.values.type}
                >
                  <Space direction="vertical">
                    {unitPriceTypes.map((unitPriceType) => {
                      return (
                        <Radio key={unitPriceType.value} value={unitPriceType.value}>
                          {t(`services.${unitPriceType.label}`)}
                        </Radio>
                      );
                    })}
                  </Space>
                </Radio.Group>
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            {formikServices.values.unitPrice === "HOUR" && (
              <Col lg={12} md={12} xs={24} sm={12}>
                <Row>
                  <label className="fw-bold  mb-10 mt-10" htmlFor="basePrice">
                    {t("services.table.basePrice")}*
                  </label>
                </Row>
                <Row className="mb-2em">
                  <Input
                    type="number"
                    required
                    id="basePrice"
                    name="basePrice"
                    onChange={formikServices.handleChange}
                    onBlur={formikServices.handleBlur}
                    value={formikServices.values.basePrice}
                  />
                </Row>
              </Col>
            )}
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold  mb-10 mt-10" htmlFor="productCategoryId">
                  {t("services.table.productCategoryId")}*
                </label>
              </Row>
              <Row className="mb-2em">
                <Select
                  allowClear
                  className="w-100"
                  id="productCategoryId"
                  name="productCategoryId"
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => formikServices.setFieldValue("productCategoryId", value)}
                  placeholder={t("services.table.productCategoryId")}
                  onBlur={formikServices.handleBlur("productCategoryId")}
                  value={formikServices.values.productCategoryId}
                >
                  {state.productCategories.length > 0 &&
                    state.productCategories.map((data) => {
                      return (
                        <Option key={data.id} value={data.id} name={data.name}>
                          {data.id}
                        </Option>
                      );
                    })}
                </Select>
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="costCentreId">
                  {t("services.table.costCentreId")}*
                </label>
              </Row>
              <Row>
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                  id="costCentreId"
                  name="costCentreId"
                  className="w-100"
                  placeholder={t("services.table.costCentreId")}
                  onChange={(value) => formikServices.setFieldValue("costCentreId", value)}
                  onBlur={formikServices.handleBlur("costCentreId")}
                  value={formikServices.values.costCentreId}
                >
                  {state.costCentres.map((cc) => (
                    <Option key={`cc-${cc.id}`} value={cc.id} name={cc.name}>
                      {cc.id}
                    </Option>
                  ))}
                </Select>
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="enabled">
                  {t("services.table.enabled")}
                </label>
              </Row>
              <Row className="mb-2em">
                <Switch
                  id="enabled"
                  name="enabled"
                  onChange={() => {
                    formikServices.setFieldValue("enabled", !formikServices.values.enabled);
                  }}
                  checked={formikServices.values.enabled}
                />
              </Row>
            </Col>
          </Row>
        </>
      </Modal>
    </>
  );
};

export default Services;
